import { ToastContextDispatch, UserInfoContext } from "Context/CommonContext";
import useMasterContext from "Context/MasterContext";
import {
  addDays,
  format,
  setHours,
  setMinutes,
  setSeconds,
  subDays,
} from "date-fns";
import { useContext, useEffect, useState } from "react";
import { useScheduleBetaContext } from "Pages/MyScheduleBeta/Context/ScheduleBetaContext";
import { toISTStart, toISTEnd } from "Pages/MyScheduleBeta/utils/dateHelpers";
import { useLocation, useNavigate } from "react-router-dom";
import { getAPIRequest } from "utilFunctions/Axios/AxiosRequestConfiguration";
import getRequestInterface from "utilFunctions/Axios/getRequestInterface";
import { EventTypes } from "utilFunctions/Constants/Constants";
import {
  BASE_URL,
  GET_ASSETS_WITH_SM_ID,
  NOT_MARKED_COMPLIANCE,
  SYLLABUS_COMPLIANCE_LIST,
} from "utilFunctions/Constants/urlConstants";
import { usePutPostAPI } from "utilFunctions/CustomHooks/usePutPostAPI";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";
import { convertTime } from "utilFunctions/helperFunctions/convertTime";
import { selectInterface } from "utilFunctions/shared/PerformanceSearchBar";
import React from "react";

const pendingFetchUrls = new Set<string>();
let lastFetchedUrl = "";
let lastFetchedTimestamp = 0;
export const getStartDate = (date: any): string =>
  format(
    setSeconds(setMinutes(setHours(subDays(new Date(date), 1), 18), 30), 0),
    "yyyy-MM-dd HH:mm:ss"
  );

export const getEndDate = (date: any): string =>
  format(
    setSeconds(setMinutes(setHours(new Date(date), 18), 29), 0),
    "yyyy-MM-dd HH:mm:ss"
  );

/** Default ±3-day window around today */
const buildInitialDateRange = () => ({
  startDate: new Date(
    format(
      setSeconds(setMinutes(setHours(subDays(new Date(), 3), 18), 30), 0),
      "yyyy-MM-dd HH:mm:ss"
    )
  ),
  endDate: new Date(
    format(
      setSeconds(setMinutes(setHours(addDays(new Date(), 3), 18), 29), 0),
      "yyyy-MM-dd HH:mm:ss"
    )
  ),
  key: "selection",
});

const syllabusStatus = [
  { label: "Marked", value: "Marked" },
  { label: "Not marked", value: "Not marked" },
  { label: "Delayed", value: "Delayed" },
  { label: "Upcoming", value: "Upcoming" },
];

export const useMyScheduleBeta = () => {
  // ── Context ────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const userInfo = useContext<any>(UserInfoContext);
  const ToastDispatch = useContext<any>(ToastContextDispatch);
  const { masters } = useMasterContext();
  const location = useLocation();
  const { state } = location;

  const {
    selectedDate,
    setSelectedDate,
    todayDate,
    selectedEventType,
    setSelectedEventType,
    isNotmarkedVisible,
    setIsNotmarkedVisible,
    setSearchedFaculty,
    activeTab,
    setActiveTab,
    todaySchedules,
    setTodaySchedules,
    fullSchedules,
    setFullSchedules,
    filterState,
    setFilterState,
  } = useScheduleBetaContext();

  const [badgeCounts, setBadgeCounts] = React.useState<{
    [key: string]: number;
  }>({
    "1": 0,
    "2": 0,
    "3": 0,
    "4": 0,
    "5": 0,
    "6": 0,
    "7": 0,
  });

  // ── Schedule state ─────────────────────────────────────────────────────────

  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  // filterState and setFilterState come from ScheduleBetaContext (shared with header)
  const [isTableFilterApplied, setIsTableFilterApplied] = useState<any>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<any>(null);
  const [lecturePrecheckedNodes, setLecturePrecheckedNodes] = useState<any>([]);
  const [subjectsList, setSubjectsList] = useState<selectInterface[]>(
    userInfo?.subjects ? userInfo.subjects : []
  );
  const [isPaneOpenDatePicker, setisPaneOpenDatePicker] =
    useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [ispaneopen, setIspaneOpen] = useState<boolean>(false);
  const [selectedlecture, setSelectedlecture] = useState<any>({});
  const [lectureAttendanceUnmarkedCount, setLectureAttendanceUnmarkedCount] =
    useState<any>({
      lecture: 0,
      attendance: 0,
    });
  const [uniqueStartTimesArray, setuniqueStartTimesArray] = useState<any>([]);
  const [statusResponseMap, setstatusResponseMap] = useState<any>([]);
  const [planneLecturedList, setplanneLecturedList] = useState<any>([]);
  const [plannedLecture, setplannedLecture] = useState<any>(null);
  const [defaultLecture, setDefaultLecture] = useState<any>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isLectureViewModal, setIsLectureViewModal] = useState<boolean>(false);

  const { makeRequest: GetAssets } = usePutPostAPI(
    (data: any) => {
      if (data && data["data"]) {
        navigate("/admin/mylibrary/" + data["data"]["smId"]);
      }
    },
    (error: any) => {
      ToastDispatch({
        isOpen: true,
        type: "Error",
        message:
          error?.["response"]?.["data"]?.["internalCode"] ||
          error?.["response"]?.["data"]?.["message"],
      });
    },
    "Post"
  );

  // ── Effects ────────────────────────────────────────────────────────────────

  /** Restore eventType filter from localStorage */
  useEffect(() => {
    if (localStorage.getItem("eventType")) {
      setSelectedEventType(localStorage.getItem("eventType")!);
    }
  }, [localStorage.getItem("eventType")]);

  /** Restore scheduledFilter or trigger initial API call */
  useEffect(() => {
    const returningFromDetails =
      (location.state as any)?.fromLectureDetails === true;
    const hasCachedData =
      fullSchedules && Object.keys(fullSchedules).length > 0;

    if (returningFromDetails && hasCachedData) {
      return;
    }

    if (
      localStorage.getItem("scheduledFilter") &&
      JSON.parse(localStorage.getItem("scheduledFilter") as any)
    ) {
      const parsedFilter = JSON.parse(
        localStorage.getItem("scheduledFilter") as any
      );
      handleSearchFaculty(parsedFilter);
    } else {
      const selectedFacultyId = filterState?.facultySearch?.employeeId || null;
      const employeeId = selectedFacultyId || userInfo.employeeId;
      setSearchedFaculty(selectedFacultyId);
      if (employeeId) {
        const filters: string[] = [];
        let formValues: any = { ...filterState };

        if (state && state.referrer && state.filterState) {
          const { startDate, endDate } = state.filterState;
          if (
            filterState.startDate === startDate &&
            filterState.endDate === endDate
          ) {
            formValues = { ...state.filterState };
          }
        }

        delete formValues["isClear"];
        delete formValues["facultySearch"];
        delete formValues["clearFacultySearchInput"];
        let newUrl = SYLLABUS_COMPLIANCE_LIST + `?facultyId=${employeeId}`;

        if (formValues === undefined) {
          newUrl =
            newUrl +
            `&startDate=${filterState.startDate}&endDate=${filterState.endDate}`;
        } else {
          if (
            state &&
            state?.isTodaySchedule &&
            formValues.startDate === undefined &&
            formValues.endDate === undefined
          ) {
            window.history.replaceState(null, "", location.pathname);
            newUrl = getUrlWithDate(newUrl, formValues);
          } else if (
            state &&
            state.isNotMarkedVisible &&
            Object.keys(formValues).length === 0 &&
            !isNotmarkedVisible
          ) {
            newUrl = NOT_MARKED_COMPLIANCE + `?facultyId=${employeeId}`;
            window.history.replaceState(null, "", location.pathname);
            setIsNotmarkedVisible(!isNotmarkedVisible);
          } else if (
            Object.keys(formValues).length &&
            formValues.startDate &&
            formValues.endDate
          ) {
            newUrl = newUrl + "";
          } else {
            if (localStorage.getItem("eventType")) {
              newUrl = getUrlWithDate(newUrl, {
                eventType: localStorage.getItem("eventType"),
              });
            } else {
              newUrl = getUrlWithDate(newUrl, formValues);
            }
          }
        }

        if (formValues) {
          formValues["startDate"] =
            formValues?.["startDate"] &&
            formValues["startDate"] !== "" &&
            formValues["startDate"] !== null
              ? formValues["startDate"]
              : null;
          formValues["endDate"] =
            formValues?.["endDate"] &&
            formValues["endDate"] !== "" &&
            formValues["endDate"] !== null
              ? formValues["endDate"]
              : null;
          formValues["subject"] = formValues?.subject
            ? typeof formValues.subject === "string"
              ? formValues.subject
              : formValues.subject?.label
            : null;
          if (localStorage.getItem("eventType")) {
            formValues["eventType"] = localStorage.getItem("eventType");
          }
        }

        for (const key in formValues) {
          if (Object.prototype.hasOwnProperty.call(formValues, key)) {
            if (formValues[key] != null && formValues[key] !== "") {
              filters.push(
                encodeURIComponent(key) +
                  "=" +
                  encodeURIComponent(formValues[key])
              );
            }
          }
        }
        if (Array.isArray(filters) && filters.length > 0) {
          newUrl = newUrl + "&" + filters.join("&");
        }
        fetchData(BASE_URL + newUrl, newUrl, employeeId);
      }
    }
  }, [userInfo?.employeeId, JSON.stringify(filterState)]);

  /** Restore filterState from router state */
  useEffect(() => {
    if (state && state.filterState) {
      setFilterState(state.filterState);
    }
  }, [location]);

  /** Build subjectsList from userInfo */
  useEffect(() => {
    if (userInfo?.subjects) {
      const newSubjectdata: selectInterface[] = [];
      userInfo.subjects.forEach((item: any) => {
        newSubjectdata.push({
          label: item.subjectName,
          value: item.id,
          key: item.id,
        });
      });
      setSubjectsList(newSubjectdata);
    }
  }, [userInfo]);

  /** GTM: view_my_schedule */
  useEffect(() => {
    if (selectedEventType) {
      pushToDataLayerForGTM({
        event: "view_my_schedule",
        date_selected: todayDate,
        view_type: selectedEventType,
        date_range_applied: selectedDateRange ?? null,
      });
    }
  }, [userInfo?.employeeId]);

  // ── URL builders ───────────────────────────────────────────────────────────

  const getUrlWithDate = (newUrl: string, formValues: any): string => {
    console.log("formValues", formValues);
    const start = state?.filterState?.startDate || filterState.startDate;
    const end = state?.filterState?.endDate || filterState.endDate;
    return newUrl + `&startDate=${start}&endDate=${end}`;
  };

  // ── APIs ───────────────────────────────────────────────────────────────────

  /**
   * Main schedule API — builds the URL from current filterState + dateRanges
   * and triggers fetchData. Pass an optional eventType override.
   */
  const CallMyScheduleApi = (eventType?: any) => {
    const employeeId =
      filterState?.facultySearch?.employeeId || userInfo.employeeId;
    let newUrl = SYLLABUS_COMPLIANCE_LIST + `?facultyId=${employeeId}`;
    const filters: string[] = [];
    const formValues: any = { ...filterState };
    delete formValues["facultySearch"];
    delete formValues["clearFacultySearchInput"];

    if (
      Object.keys(formValues).length &&
      formValues.startDate &&
      formValues.endDate
    ) {
      newUrl = newUrl + "";
    } else {
      const initialRange = buildInitialDateRange();
      const start = toISTStart(initialRange.startDate);
      const end = toISTEnd(initialRange.endDate);
      newUrl = newUrl + `&startDate=${start}&endDate=${end}`;
    }

    if (formValues) {
      formValues["startDate"] =
        formValues?.["startDate"] &&
        formValues["startDate"] !== "" &&
        formValues["startDate"] !== null
          ? formValues["startDate"]
          : null;
      formValues["endDate"] =
        formValues?.["endDate"] &&
        formValues["endDate"] !== "" &&
        formValues["endDate"] !== null
          ? formValues["endDate"]
          : null;
      formValues["subject"] = formValues?.subject
        ? typeof formValues.subject === "string"
          ? formValues.subject
          : formValues.subject?.label
        : null;
    }

    if (eventType) {
      formValues["eventType"] = eventType;
    }

    for (const key in formValues) {
      if (Object.prototype.hasOwnProperty.call(formValues, key)) {
        if (formValues[key] != null && formValues[key] !== "") {
          filters.push(
            encodeURIComponent(key) + "=" + encodeURIComponent(formValues[key])
          );
        }
      }
    }
    if (Array.isArray(filters) && filters.length > 0) {
      newUrl = newUrl + "&" + filters.join("&");
    }
    fetchData(BASE_URL + newUrl, newUrl, employeeId);
  };

  /** Fetch planned lecture details for TOC modal */
  const getPlannedLecture = async (url: string) => {
    setLoading(true);
    await getAPIRequest({
      url,
      success: (response: any) => {
        if (response?.["data"]?.["data"]) {
          setplanneLecturedList(
            response["data"]["data"]["plannedLectureIdList"]
          );
          setplannedLecture(response["data"]["data"]);
        } else {
          setplanneLecturedList([]);
          setplannedLecture([]);
        }
        setLoading(false);
      },
      failure: (error: any) => {
        setplanneLecturedList([]);
        setplannedLecture([]);
        setLoading(false);
        ToastDispatch({
          isOpen: true,
          type: "Error",
          message:
            error?.response?.data?.code === "NF"
              ? "No Topic Present for this lecture"
              : error.message,
        });
      },
    } as getRequestInterface);
  };

  /**
   * Core data fetch — populates schedule list.
   * Selects today's bucket when available, otherwise falls back to the last date.
   */
  const fetchData = async (
    url: string,
    newUrl: string,
    employeeId: any,
    force = false
  ) => {
    const now = Date.now();
    if (
      !force &&
      (pendingFetchUrls.has(url) ||
        (lastFetchedUrl === url && now - lastFetchedTimestamp < 1000))
    ) {
      return;
    }
    pendingFetchUrls.add(url);
    lastFetchedUrl = url;
    lastFetchedTimestamp = now;

    setLoading(true);
    await getAPIRequest({
      url,
      success: (response: any) => {
        pendingFetchUrls.delete(url);
        const responseData = response?.data?.data?.finalResponseMap;
        setFullSchedules(responseData ?? {});
        if (responseData && responseData[todayDate] !== undefined) {
          const targetDate =
            selectedDate && responseData[selectedDate]
              ? selectedDate
              : todayDate;
          setTodaySchedules(responseData[targetDate]);
          setDefaultLecture({
            label: convertTime(todayDate, "D MMM, ddd"),
            value: todayDate,
          });
          setData(response?.data?.data);
          setuniqueStartTimesArray(Object.keys(responseData));
          setstatusResponseMap(response?.data?.data?.statusResponseMap);
          setLectureAttendanceUnmarkedCount({
            lecture: response?.data?.data?.totalUnmarkedLectures,
            attendance: response?.data?.data?.totalUnmarkedAttendanceCount,
          });
          setSelectedDate(targetDate);
        } else {
          const dataArray = Object.keys(
            response?.data?.data?.finalResponseMap ?? {}
          );
          const lastIndex = dataArray[dataArray.length - 1];
          setTodaySchedules(responseData?.[lastIndex] ?? []);
          const eventType = localStorage.getItem("eventType");
          console.log(
            filterState,
            eventType,
            "activeDate===",
            lastIndex,
            todayDate
          );
          setDefaultLecture({
            label: convertTime(todayDate, "D MMM, ddd"),
            value: todayDate,
          });
          setData(response?.data?.data);
          setstatusResponseMap(response?.data?.data?.statusResponseMap);
          setuniqueStartTimesArray(Object.keys(responseData ?? {}));
          setLectureAttendanceUnmarkedCount({
            lecture: response?.data?.data?.totalUnmarkedLectures,
            attendance: response?.data?.data?.totalUnmarkedAttendanceCount,
          });
        }
        setTimeout(() => setLoading(false), 300);
      },
      failure: (error: any) => {
        pendingFetchUrls.delete(url);
        if (
          error?.["response"]?.["data"]?.["internalCode"] &&
          error["response"]["data"]["internalCode"] !== "1008"
        ) {
          ToastDispatch({
            isOpen: true,
            type: "Error",
            message: error.message,
          });
        } else if (
          error?.["response"]?.["data"]?.["code"] === "NF" &&
          employeeId !== userInfo?.employeeId
        ) {
          ToastDispatch({
            isOpen: true,
            type: "Error",
            message: "No record found for searched faculty",
          });
        }
        setTodaySchedules([]);
        setFullSchedules({});
        setuniqueStartTimesArray([]);
        setData([]);
        setstatusResponseMap([]);
        setLectureAttendanceUnmarkedCount({ lecture: 0, attendance: 0 });
        setTimeout(() => setLoading(false), 300);
      },
    } as getRequestInterface);
  };

  // ── Utility functions ─────────────────────────────────────────────────────

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  /** Called when a date card is clicked in the calendar strip */
  const handleDateSelection = (item: any) => {
    const todayEvent = data?.finalResponseMap?.[item];
    setSelectedDate(item);
    setTodaySchedules(todayEvent);
  };

  /** Pushes GTM events for each applied filter */
  const handleAppliedFilter = (args: any) => {
    const filterEntries = Object.entries(args).filter(
      ([, v]) => v != null && v !== ""
    );
    filterEntries.forEach(([filterType, filterValue]) => {
      pushToDataLayerForGTM({
        event: "filter_my_schedule",
        filter_type: filterType,
        filter_value: String(filterValue),
      });
    });
  };

  /** Opens mark-syllabus pane or lecture-view modal based on action */
  const handleMarkSyllbus = (item: any, action: any) => {
    if (action === "mark") {
      pushToDataLayerForGTM({
        event: "click_mark_syllabus_my_schedule",
        lecture_id: item?.fexRecordId ?? item?.batchTimeTableId,
        batch_id: item?.batchId,
        source: "my_schedule",
      });
      setIspaneOpen(true);
      getNotMarkerdChapter(item);
    }
    if (action === "view") {
      pushToDataLayerForGTM({
        event: "view_lecture_details_my_schedule",
        lecture_id: item?.fexRecordId ?? item?.batchTimeTableId,
        batch_id: item?.batchId,
        lecture_status: item?.syllabusStatus,
      });
      setIsLectureViewModal(!isLectureViewModal);
      setSelectedlecture(item);
      return;
    }
    setSelectedlecture(item);
  };

  const getNotMarkerdChapter = (item: any) => {
    console.log(item);
  };

  const toggleModal = () => {
    setIspaneOpen(!ispaneopen);
  };

  const toggle = () => setTooltipOpen(!tooltipOpen);

  /** Closes TOC marker pane and optionally navigates back */
  const handleCloseTOCMarker = () => {
    setIspaneOpen(!ispaneopen);
    setLecturePrecheckedNodes([]);
    if (location.state?.referrer) {
      navigate(location.state.referrer, {
        replace: true,
        state: { ...location.state },
      });
    } else {
      setLoading(false);
    }
  };

  /** Called after TOC modal successfully closes */
  const modalCloseHandler = () => {
    setLecturePrecheckedNodes([]);
    toggleModal();
    if (location.state?.referrer) {
      navigate(location.state.referrer, {
        replace: true,
        state: { ...location.state },
      });
    } else {
      if (isNotmarkedVisible) {
        const employeeId =
          filterState?.facultySearch?.employeeId || userInfo.employeeId;
        const newUrl = NOT_MARKED_COMPLIANCE + `?facultyId=${employeeId}`;
        fetchData(BASE_URL + newUrl, newUrl, employeeId);
      } else {
        const eventType = localStorage.getItem("eventType")
          ? localStorage.getItem("eventType")
          : selectedEventType;
        CallMyScheduleApi(eventType);
      }
    }
  };

  /** "Not Marked" checkbox toggle */
  const NotMarkedChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const notMarked = event.target.checked;
    setIsNotmarkedVisible(notMarked);
    if (notMarked) {
      const employeeId =
        filterState?.facultySearch?.employeeId || userInfo.employeeId;
      const newUrl = NOT_MARKED_COMPLIANCE + `?facultyId=${employeeId}`;
      fetchData(BASE_URL + newUrl, newUrl, employeeId);
    } else {
      CallMyScheduleApi();
    }
  };

  /** Navigates to the asset library for a given card */
  const detailShowHandler = (cardData: any) => {
    if (!cardData?.fexRecordId || !cardData?.courseId) {
      ToastDispatch({
        isOpen: true,
        type: "Warning",
        message: "Missing required information to show details.",
      });
      return;
    }
    const obj = {
      lectureId: cardData?.fexRecordId,
      showAssets: true,
      courseId: cardData?.courseId,
    };
    const url = BASE_URL + GET_ASSETS_WITH_SM_ID;
    GetAssets(url, obj);
  };

  /** Pre-fills checked nodes for syllabus edit */
  const editHandler = (lectureAssetsData: any) => {
    pushToDataLayerForGTM({
      event: "edit_syllabus",
      lecture_id:
        selectedlecture?.fexRecordId ?? selectedlecture?.batchTimeTableId,
      nodes_edited: lectureAssetsData?.length ?? 0,
    });
    if (
      selectedlecture["revisionRank"] &&
      selectedlecture["revisionRank"] != null &&
      selectedlecture["revisionRank"] !== 0
    ) {
      const ele = "revision_" + selectedlecture["revisionRank"];
      setLecturePrecheckedNodes([ele]);
    } else {
      const lectureAssetsDataIds = lectureAssetsData.map(
        (item: any) => item.nodeId
      );
      setLecturePrecheckedNodes(lectureAssetsDataIds);
    }
    handleMarkSyllbus(selectedlecture, "mark");
  };

  /**
   * Returns the filtered schedule list for the selected date.
   * When "Not Marked" toggle is on, returns everything unchanged.
   */
  const returnTodaySchedules = (schedules: any, eventType: string) => {
    if (isNotmarkedVisible) return schedules;
    if (eventType === "ALL") return schedules;
    return schedules?.filter((ele: any) => ele.eventType === eventType);
  };

  /** Resets all filters to defaults */
  const setInitialFilters = (suggest?: any) => {
    const initialDateRange = buildInitialDateRange();
    const SD = getStartDate(initialDateRange.startDate);
    const ED = getEndDate(initialDateRange.endDate);
    const employeeId =
      suggest && suggest.employeeId ? suggest.employeeId : userInfo.employeeId;
    let newUrl =
      SYLLABUS_COMPLIANCE_LIST +
      `?facultyId=${employeeId}` +
      `&startDate=${SD}&endDate=${ED}`;
    if (selectedEventType) {
      newUrl = newUrl + "&eventType=" + selectedEventType;
    }
    fetchData(BASE_URL + newUrl, newUrl, employeeId);
    setFilterState({
      eventType: EventTypes[0]?.value ?? "ALL",
      startDate: SD,
      endDate: ED,
    });
  };

  /** Searches schedule for a different faculty */
  const handleSearchFaculty = (suggest: any) => {
    if (filterState && filterState.isClear) {
      setInitialFilters(suggest);
    } else {
      const formValues: any = { ...filterState };
      const defaultRange = buildInitialDateRange();
      const defaultSD = toISTStart(defaultRange.startDate);
      const startdate = formValues["startDate"]
        ? formValues["startDate"]
        : filterState.startDate || defaultSD;

      const defaultED = toISTEnd(defaultRange.endDate);
      const endDate = formValues["endDate"]
        ? formValues["endDate"]
        : filterState.endDate || defaultED;

      const subj = formValues["subject"]
        ? formValues?.subject?.label ?? null
        : undefined;

      if (suggest && suggest != null) {
        let SD: any = startdate;
        let ED: any = endDate;
        if (state && state.referrer && state.filterState) {
          SD = state.filterState.startDate;
          ED = state.filterState.endDate;
        }
        setSearchedFaculty(suggest.employeeId);
        const employeeId = suggest.employeeId;
        let newUrl =
          SYLLABUS_COMPLIANCE_LIST +
          `?facultyId=${employeeId}` +
          `&startDate=${SD}&endDate=${ED}`;
        if (subj) newUrl = newUrl + "&subject=" + subj;
        if (selectedEventType)
          newUrl = newUrl + "&eventType=" + selectedEventType;
        fetchData(BASE_URL + newUrl, newUrl, employeeId);
      } else {
        const startD = filterState.startDate || startdate;
        const endD = filterState.endDate || endDate;
        setSearchedFaculty(null);
        const employeeId = userInfo.employeeId;
        let newUrl =
          SYLLABUS_COMPLIANCE_LIST +
          `?facultyId=${employeeId}` +
          `&startDate=${startD}&endDate=${endD}`;
        if (subj) newUrl = newUrl + "&subject=" + subj;
        if (selectedEventType)
          newUrl = newUrl + "&eventType=" + selectedEventType;
        fetchData(BASE_URL + newUrl, newUrl, employeeId);
      }
    }
  };

  // ── Return ─────────────────────────────────────────────────────────────────

  return {
    // Tab
    activeTab,
    badgeCounts,
    setBadgeCounts,
    handleTabChange,

    // Data & loading
    data,
    loading,
    todayDate,
    todaySchedules,
    fullSchedules,
    uniqueStartTimesArray,
    statusResponseMap,
    lectureAttendanceUnmarkedCount,
    planneLecturedList,
    plannedLecture,
    defaultLecture,
    setDefaultLecture,

    // Filter state
    filterState,
    setFilterState,
    selectedItem: selectedEventType,
    setselectedItem: setSelectedEventType,
    selectedDate,
    isNotmarkedVisible,
    setIsNotmarkedVisible,
    isTableFilterApplied,
    setIsTableFilterApplied,
    selectedDateRange,
    setSelectedDateRange,
    subjectsList,
    syllabusStatus,
    masters,

    // Pane / modal state
    ispaneopen,
    setIspaneOpen,
    open,
    setOpen,
    isPaneOpenDatePicker,
    setisPaneOpenDatePicker,
    isLectureViewModal,
    setIsLectureViewModal,
    selectedlecture,
    setSelectedlecture,
    lecturePrecheckedNodes,
    setLecturePrecheckedNodes,
    tooltipOpen,
    toggle,

    // APIs
    CallMyScheduleApi,
    fetchData,
    getPlannedLecture,

    // Handlers
    handleDateSelection,
    handleAppliedFilter,
    handleMarkSyllbus,
    handleCloseTOCMarker,
    modalCloseHandler,
    NotMarkedChangeHandler,
    detailShowHandler,
    editHandler,
    returnTodaySchedules,
    handleSearchFaculty,
    setInitialFilters,
    toggleModal,

    // URL helpers (exported in case components need them)
    getUrlWithDate,
  };
};
